import * as R from 'ramda';
import { useCallback, useEffect } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import LangEnum from '../lang/LangEnum';
import { usePageTitleContext } from '../modules/context/PageTitle.context';
import { OUR_BRAND_NAME } from '../modules/reports/utils/consts';
import { useTranslation } from '../modules/translation/TranslationContext';
import PATHS from '../routes/routes.const';
import { RouteConfig } from '../routes/routes.types';
import { uuidRegex } from '../utils/regexes';
import { flattenRoutes } from '../utils/routes/flattenRouter.utils';
import { containsNewPathSegment } from '../utils/routes/searchElementsRoutes.utils';

const allFlatRoutes: RouteConfig[] = flattenRoutes(PATHS);

interface UsePageTitleManagerProps {
  dynamicPart?: string;
  overridePageTitleKey?: LangEnum;
}

export const usePageTitleManager = ({
  dynamicPart = '',
  overridePageTitleKey,
}: UsePageTitleManagerProps) => {
  const { translate, isMessagesLoaded } = useTranslation();
  const location = useLocation();
  const {
    dynamicPart: contextDynamicPart,
    setDynamicPart,
    overrideTitle,
    setOverrideTitle,
  } = usePageTitleContext();

  useEffect(() => {
    if (dynamicPart) {
      setDynamicPart(dynamicPart);
    }

    if (overridePageTitleKey) {
      setOverrideTitle(overridePageTitleKey);
    }

    return () => {
      if (contextDynamicPart) {
        setDynamicPart('');
      }
      if (overridePageTitleKey) {
        setOverrideTitle('');
      }
    };
  }, [dynamicPart, contextDynamicPart, overridePageTitleKey, setDynamicPart, setOverrideTitle]);

  const buildPageTitle = useCallback(() => {
    const matchedEntry = allFlatRoutes.find((routeConfig) =>
      matchPath(location.pathname, {
        path: routeConfig.path,
        exact: true,
      }),
    );

    const hasPageTitle = !R.isNil(matchedEntry?.pageTitleKey);
    const baseTitleKey: LangEnum | string =
      overrideTitle || (matchedEntry?.pageTitleKey as LangEnum) || LangEnum.GLOBAL_TECEO;

    const correctedDynamicPart =
      location.pathname.match(uuidRegex) && contextDynamicPart
        ? contextDynamicPart.toLowerCase()
        : '';

    const suffixTitleKey: LangEnum | null =
      containsNewPathSegment(location.pathname) && hasPageTitle
        ? LangEnum.GLOBAL_ADD
        : matchedEntry?.suffixTitleKey || null;

    const titleParts: string[] = [translate(baseTitleKey as LangEnum)];

    if (suffixTitleKey) {
      titleParts.push(`- ${translate(suffixTitleKey)}`);
    } else if (correctedDynamicPart) {
      titleParts.push(correctedDynamicPart);
    }

    return isMessagesLoaded ? titleParts.join(' ') : OUR_BRAND_NAME;
  }, [location.pathname, contextDynamicPart, overrideTitle, isMessagesLoaded, translate]);

  useEffect(() => {
    document.title = buildPageTitle();
  }, [buildPageTitle]);
};
